import { useState, useEffect, useCallback } from 'react';
import { ventasApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { FileText, X, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../../lib/utils';

const fmt = (num) => (num || 0).toLocaleString('es-CL');

// Iter 294.b (Carlos): listado de facturas/NC de venta sin OT asignada,
// con asignación de OT inline. Se abre desde el banner del Activo Circulante.
export const FacturasSinOTModal = ({ open, onClose, onAssigned }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filtroAnio, setFiltroAnio] = useState(0);
  const [incluirSinMonto, setIncluirSinMonto] = useState(false);
  const [otInputs, setOtInputs] = useState({});
  const [asignando, setAsignando] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ventasApi.getSinOT({ anio: filtroAnio, incluir_sin_monto: incluirSinMonto });
      setData(res.data);
    } catch {
      toast.error('Error al cargar facturas sin OT');
    } finally {
      setLoading(false);
    }
  }, [filtroAnio, incluirSinMonto]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleAsignar = async (venta) => {
    const otNum = parseInt(otInputs[venta.id_venta], 10);
    if (!otNum) {
      toast.error('Ingresa un N° de OT válido');
      return;
    }
    const numFac = String(venta.numero_factura || '').replace(/\.0$/, '');
    if (!window.confirm(`¿Asignar la factura N° ${numFac} a la OT-${otNum}?`)) return;
    setAsignando(venta.id_venta);
    try {
      const res = await ventasApi.asignarOT(venta.id_venta, otNum);
      toast.success(`Factura ${numFac} asignada a OT-${otNum} · ${res.data.ot_descripcion}`);
      setOtInputs((prev) => ({ ...prev, [venta.id_venta]: '' }));
      load();
      onAssigned?.();
    } catch (_err) {
      toast.error(_err?.response?.data?.detail || 'Error al asignar la OT');
    } finally {
      setAsignando(null);
    }
  };

  if (!open) return null;

  const anioActual = data?.anio_actual || new Date().getFullYear();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full flex flex-col max-h-[calc(100vh-3rem)]" data-testid="modal-facturas-sin-ot">
        <div className="flex justify-between items-center p-3 border-b bg-amber-50 sticky top-0 z-10 rounded-t-lg">
          <h3 className="font-bold text-sm flex items-center gap-2 text-amber-900">
            <FileText className="w-4 h-4" />
            Facturas / NC sin OT asignada
            {data && <span className="font-normal text-xs">— {data.cantidad} documentos · ${fmt(data.total_neto)} neto</span>}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0" data-testid="close-sin-ot-modal">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-3 px-3 py-2 border-b text-xs bg-gray-50">
          <label className="flex items-center gap-1">
            Año:
            <select
              value={filtroAnio}
              onChange={(e) => setFiltroAnio(parseInt(e.target.value, 10))}
              className="h-7 rounded border border-input bg-white px-2 text-xs"
              data-testid="filtro-anio-sin-ot"
            >
              <option value={0}>Todos</option>
              <option value={anioActual}>{anioActual} (año actual)</option>
              <option value={anioActual - 1}>{anioActual - 1}</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={incluirSinMonto}
              onChange={(e) => setIncluirSinMonto(e.target.checked)}
              data-testid="check-incluir-sin-monto"
            />
            Incluir registros sin monto (histórico Access)
          </label>
          <Button onClick={load} variant="outline" size="sm" className="h-6 w-6 p-0 ml-auto">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 p-3">
          {loading ? (
            <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : !data?.ventas?.length ? (
            <p className="text-center py-8 text-muted-foreground text-sm">No hay facturas sin OT para el filtro seleccionado ✓</p>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-200 text-left">
                  <th className="border p-1">N° Factura</th>
                  <th className="border p-1">Fecha</th>
                  <th className="border p-1">Cliente</th>
                  <th className="border p-1 text-right">Neto</th>
                  <th className="border p-1 text-right">Total</th>
                  <th className="border p-1">Estado</th>
                  <th className="border p-1 w-40">Asignar OT</th>
                </tr>
              </thead>
              <tbody>
                {data.ventas.map((v, idx) => {
                  const cancel = v.cancelado === 1 || v.cancelado === true || v.cancelado === '1';
                  return (
                    <tr key={v.id_venta || idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${cancel ? 'opacity-60' : ''}`}>
                      <td className={`border p-1 font-mono ${cancel ? 'line-through' : ''}`}>{String(v.numero_factura || '').replace(/\.0$/, '')}</td>
                      <td className="border p-1">{v.fecha_emision || v.fecha ? fmtFecha(v.fecha_emision || v.fecha) : '-'}</td>
                      <td className="border p-1">{v.cliente_nombre || (v.id_cliente ? `Cliente ${v.id_cliente}` : '-')}</td>
                      <td className="border p-1 text-right font-mono">${fmt(v.neto)}</td>
                      <td className="border p-1 text-right font-mono">${fmt(v.total)}</td>
                      <td className="border p-1">
                        {cancel ? <span className="text-red-600 font-semibold">Anulada</span> : <span className="text-gray-600">{v.estado || 'Emitida'}</span>}
                      </td>
                      <td className="border p-1">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            placeholder="N° OT"
                            value={otInputs[v.id_venta] || ''}
                            onChange={(e) => setOtInputs((prev) => ({ ...prev, [v.id_venta]: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && handleAsignar(v)}
                            className="h-6 w-20 text-xs p-1"
                            data-testid={`input-ot-${v.id_venta}`}
                          />
                          <Button
                            size="sm"
                            className="h-6 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                            disabled={asignando === v.id_venta || !otInputs[v.id_venta]}
                            onClick={() => handleAsignar(v)}
                            data-testid={`btn-asignar-ot-${v.id_venta}`}
                          >
                            {asignando === v.id_venta ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacturasSinOTModal;
